import request from "../utils/request"

export const getAdminDashboard = () => request.get("/admin/dashboard")

// User management endpoints
export const getAdminUsers = (params = {}) => {
	const queryString = new URLSearchParams(params).toString()
	return request.get(`/admin/users${queryString ? "?" + queryString : ""}`)
}

export const getUserMonitoring = (userId, params = {}) => {
	const queryString = new URLSearchParams(params).toString()
	return request.get(`/admin/users/${userId}/monitoring${queryString ? "?" + queryString : ""}`)
}

// Sign-in/out history for one user -- student, parent or admin -- so the owner
// can tell whether someone else got into their account and from where. Every
// read is access-logged server-side (feature/audit-logging,
// ../wicked-maths-laravel), same as the GOT IT audit above.
export const getLoginHistory = (userId, params = {}) => {
	const queryString = new URLSearchParams(params).toString()
	return request.get(`/admin/users/${userId}/login-events${queryString ? "?" + queryString : ""}`)
}

// "Got It" / "Reveal Answer" click-interval audit. params: { start, end } as
// YYYY-MM-DD UK dates. `sources` is an optional array of "got_it" /
// "reveal_answer" -- omit it to keep the server's ["got_it"] default. Every
// call is logged server-side against the admin who made it.
//
// URLSearchParams(params).toString() would comma-join an array value
// (sources=got_it%2Creveal_answer), which Laravel's `array` validation
// rejects -- each element has to be appended under the repeated literal key
// `sources[]` instead, so the query string decodes as an array on the PHP side.
const buildAuditQuery = (params, extra = {}) => {
	const { sources, ...rest } = params
	const qs = new URLSearchParams({ ...rest, ...extra })
	if (sources) {
		sources.forEach(source => qs.append("sources[]", source))
	}
	return qs.toString()
}

export const getGotItAudit = (userId, params = {}) => {
	const queryString = buildAuditQuery(params)
	return request.get(`/admin/users/${userId}/got-it-audit${queryString ? "?" + queryString : ""}`)
}

export const downloadGotItAudit = (userId, params = {}) => {
	const queryString = buildAuditQuery(params, { format: "xlsx" })
	return request.get(`/admin/users/${userId}/got-it-audit?${queryString}`, {}, { responseType: "blob" })
}

/*
 * Resetting a student's GOT ITs, with undo
 * (../wicked-maths-laravel/docs/GOT_IT_RESETS.md).
 *
 * scope is "module", "lesson_group", "lesson" or "question". A reset must quote
 * the count its preview returned as expectedCount; the server refuses it with a
 * 422 if the student has clicked since. Reading a module is access-logged.
 */
export const getStudentModuleGotIts = (userId, moduleId) =>
	request.get(`/admin/users/${userId}/got-its?module_id=${moduleId}`)

export const getGotItResets = userId => request.get(`/admin/users/${userId}/got-it-resets`)

export const previewGotItReset = (userId, scope, scopeId) =>
	request.post(`/admin/users/${userId}/got-it-resets/preview`, { scope, scope_id: scopeId })

export const resetGotIts = (userId, { scope, scopeId, expectedCount, reason }) =>
	request.post(`/admin/users/${userId}/got-it-resets`, {
		scope,
		scope_id: scopeId,
		expected_count: expectedCount,
		reason: reason ? reason : null,
	})

export const undoGotItReset = (userId, resetId) =>
	request.post(`/admin/users/${userId}/got-it-resets/${resetId}/undo`, {})

/*
 * Closing an area (module, lesson group or lesson) for one student, with re-open
 * (owner feedback F5, ../wicked-maths-laravel/docs/AREA_CLOSURES.md).
 *
 * scope is "module", "lesson_group" or "lesson". A closed area reads 100% for
 * that student, frozen when it was closed, and they cannot open it; the server
 * enforces that. The preview writes nothing. Closing an already-closed area, or
 * re-opening an already re-opened one, is refused with a 422.
 */
export const getAreaClosures = userId => request.get(`/admin/users/${userId}/area-closures`)

export const previewAreaClosure = (userId, scope, scopeId) =>
	request.post(`/admin/users/${userId}/area-closures/preview`, { scope, scope_id: scopeId })

export const closeArea = (userId, { scope, scopeId, reason }) =>
	request.post(`/admin/users/${userId}/area-closures`, {
		scope,
		scope_id: scopeId,
		reason: reason ? reason : null,
	})

export const reopenArea = (userId, closureId) =>
	request.post(`/admin/users/${userId}/area-closures/${closureId}/reopen`, {})

/*
 * "View As" -- become this student for real
 * (../wicked-maths-laravel/docs/VIEW_AS_USER.md).
 *
 * Answers { data: { token, user }, viewing_as: {...} }. The token IS the
 * student: swapping it in makes this browser that student until it is stopped,
 * in every tab. 403 if the target is not a student, 404 if there is no such
 * account. The stop half is stopViewAs() in domain/user.js, which cannot live
 * here because by then the caller is no longer an admin.
 */
export const startViewAs = userId => request.post(`/admin/view-as/${userId}`, {})

// Monitoring overview endpoint
export const getMonitoringOverview = (params = {}) => {
	const queryString = new URLSearchParams(params).toString()
	return request.get(`/admin/monitoring/overview${queryString ? "?" + queryString : ""}`)
}

// Student groups endpoints
export const getStudentGroups = () => request.get("/admin/student-groups")

export const getStudentGroup = id => request.get(`/admin/student-groups/${id}`)

export const createStudentGroup = data => request.post("/admin/student-groups", data)

export const updateStudentGroup = (id, data) => request.put(`/admin/student-groups/${id}`, data)

export const deleteStudentGroup = id => request.delete(`/admin/student-groups/${id}`)

export const addStudentGroupMembers = (groupId, memberIds) =>
	request.post(`/admin/student-groups/${groupId}/members`, { member_ids: memberIds })

export const removeStudentGroupMember = (groupId, userId) =>
	request.delete(`/admin/student-groups/${groupId}/members/${userId}`)

// Admin push subscription endpoints
export const getAdminPushSubscriptions = (params = {}) => {
	const queryString = new URLSearchParams(params).toString()
	return request.get(`/admin/push-subscriptions${queryString ? "?" + queryString : ""}`)
}

export const getAdminPushSubscription = id => request.get(`/admin/push-subscriptions/${id}`)

export const deleteAdminPushSubscription = id => request.delete(`/admin/push-subscriptions/${id}`)

export const sendTestToSubscription = (id, title, body) =>
	request.post(`/admin/push-subscriptions/${id}/test`, { title, body })

// Admin messages endpoints
export const getAdminMessages = params => request.get("/admin/messages", params)

export const getMessageFilterOptions = () => request.get("/admin/messages/filter-options")

// Admin Stripe subscription endpoints
export const getStripeStatus = () => request.get("/admin/subscriptions/status")

export const getAdminSubscriptions = (params = {}) => {
	const queryString = new URLSearchParams(params).toString()
	return request.get(`/admin/subscriptions${queryString ? "?" + queryString : ""}`)
}

export const getSubscriptionDetail = userId => request.get(`/admin/subscriptions/users/${userId}`)

export const getWebhookEvents = (params = {}) => {
	const queryString = new URLSearchParams(params).toString()
	return request.get(`/admin/subscriptions/webhooks${queryString ? "?" + queryString : ""}`)
}

export const getWebhookDetail = id => request.get(`/admin/subscriptions/webhooks/${id}`)

export const getUpcomingRenewals = (days = 7) => request.get(`/admin/subscriptions/upcoming-renewals?days=${days}`)
